import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle as DlgTitle } from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { FileUploadSection } from "@/components/common/FileUploadSection";
import { ImageViewer } from "@/components/common/ImageViewer";
import { Lead, DisbursementRecord, TimelineItem, CONNECTOR_TYPE_LABELS, LOAN_TYPE_LABELS, LoanType, DocumentAttachment, getLegacyCustomerName } from "@/types";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useAppConfig } from "@/hooks/useAppConfig";
import { useBankers } from "@/hooks/useBankers";
import { useChannelPartners } from "@/hooks/useChannelPartners";
import { addLeadStageChange } from "@/services/stageHistoryService";
import { useFileUploadManager } from "@/hooks/useFileUploadManager";
import { uploadFileToStorage } from "@/services/storageService";
import { Edit, DollarSign, TrendingUp, ArrowLeft, Share2, FileText, Eye, Download } from "lucide-react";
import { format } from "date-fns";
import { useModal } from "@/contexts/ModalContext";

interface DisbursementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead;
  onSave: (updatedLead: Partial<Lead>) => void;
}

function formatCurrency(amount: number) {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(0)}L`;
  return `₹${amount.toLocaleString("en-IN")}`;
}

// Format number with Indian comma system (e.g., 50,00,000)
function formatIndianNumber(value: string | number): string {
  if (!value) return '';
  const numStr = value.toString().replace(/,/g, '');
  if (isNaN(Number(numStr))) return value.toString();
  
  const num = parseFloat(numStr);
  return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

// Parse Indian formatted number back to plain number
function parseIndianNumber(value: string): string {
  return value.replace(/,/g, '');
}

type DisbursementType = 'full' | 'partial' | null;
type ViewMode = 'list' | 'form';

export function DisbursementDialog({ open, onOpenChange, lead, onSave }: DisbursementDialogProps) {
  const { user } = useAuth();
  const { config } = useAppConfig();
  const { bankers, isFavorite: isBankerFavorite } = useBankers();
  const { partners: channelPartners = [] } = useChannelPartners();
  const { registerModal, unregisterModal } = useModal();
  useEffect(() => {
    if (!open) return;
    registerModal('lead-disbursement-dialog', () => onOpenChange(false), 1);
    return () => unregisterModal('lead-disbursement-dialog');
  }, [open, onOpenChange, registerModal, unregisterModal]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [disbursementType, setDisbursementType] = useState<DisbursementType>(null);
  const [editingDisbursement, setEditingDisbursement] = useState<DisbursementRecord | null>(null);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  // File upload manager
  const fileUpload = useFileUploadManager({
    maxSizeMB: 10,
    allowedFileTypes: ['image/*', '.pdf', '.doc', '.docx', '.xls', '.xlsx'],
  });
  
  // Share popup state
  const [sharePopupOpen, setSharePopupOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState("");
  const [shareData, setShareData] = useState<{
    type: 'partial' | 'full';
    partialIndex?: number; // 1-based index among partial disbursements
    customerName: string;
    loanAmount: string;
    product: string;
    applicationNo: string;
    bankName: string;
    bankerName: string;
    sourcing: string;
    vendorName: string;
  } | null>(null);
  // Pending share data — set after new submit so popup opens only after form collapses to list
  const [pendingShareData, setPendingShareData] = useState<{
    type: 'partial' | 'full';
    partialIndex?: number;
    customerName: string;
    loanAmount: string;
    product: string;
    applicationNo: string;
    bankName: string;
    bankerName: string;
    sourcing: string;
    vendorName: string;
  } | null>(null);
  
  const [formData, setFormData] = useState({
    sanctionedAmount: "",
    disbursedAmount: "",
    disbursementDate: "",
    referenceNumber: "",
    remarks: "",
    salesConsiderationDate: "",
    vendorPayout: "",
    isFinal: false,
    bankName: "",
    bankerId: "",
    sourcingType: "self" as "self" | "vendor",
    vendorId: "",
  });

  // Calculate disbursement summary
  const sanctionedAmount = lead.sanctionedAmount || 0;
  const totalDisbursed = lead.disbursements?.reduce((sum, d) => sum + d.disbursedAmount, 0) || 0;
  const remainingAmount = sanctionedAmount - totalDisbursed;
  const disbursementProgress = sanctionedAmount 
    ? ((totalDisbursed / sanctionedAmount) * 100) 
    : 0;
  const hasPartialDisbursements = lead.disbursements && lead.disbursements.length > 0;

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setViewMode('list');
      setDisbursementType(null);
      setEditingDisbursement(null);
      setSharePopupOpen(false);
      setShareData(null);
      setPendingShareData(null);
      setFormData({
        sanctionedAmount: "",
        disbursedAmount: "",
        disbursementDate: "",
        referenceNumber: "",
        remarks: "",
        salesConsiderationDate: "",
        vendorPayout: "",
        isFinal: false,
        bankName: "",
        bankerId: "",
        sourcingType: "self",
        vendorId: "",
      });
    }
  }, [open]);

  // Open share popup only after form has collapsed back to list view
  useEffect(() => {
    if (viewMode === 'list' && pendingShareData) {
      setShareData(pendingShareData);
      setShareMessage(buildShareMessage(pendingShareData));
      setSharePopupOpen(true);
      setPendingShareData(null);
    }
  }, [viewMode, pendingShareData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load data when editing
  useEffect(() => {
    if (editingDisbursement) {
      setFormData({
        sanctionedAmount: editingDisbursement.sanctionedAmount?.toString() || lead.sanctionedAmount?.toString() || "",
        disbursedAmount: editingDisbursement.disbursedAmount.toString(),
        disbursementDate: format(editingDisbursement.disbursementDate, 'yyyy-MM-dd'),
        referenceNumber: editingDisbursement.referenceNumber || "",
        remarks: editingDisbursement.remarks || "",
        salesConsiderationDate: editingDisbursement.salesConsiderationDate 
          ? format(editingDisbursement.salesConsiderationDate, 'yyyy-MM-dd') 
          : "",
        vendorPayout: editingDisbursement.vendorPayout?.toString() || "",
        isFinal: editingDisbursement.isFinal,
        bankName: editingDisbursement.bankName || "",
        bankerId: editingDisbursement.bankerId || "",
        sourcingType: editingDisbursement.sourcingType || "self",
        vendorId: editingDisbursement.vendorId || "",
      });
      setDisbursementType(editingDisbursement.type);
      setViewMode('form');
    }
  }, [editingDisbursement, lead.sanctionedAmount]);

  const handleEditDisbursement = (disbursement: DisbursementRecord) => {
    setEditingDisbursement(disbursement);
  };

  const handleAddNew = (type: DisbursementType) => {
    setDisbursementType(type);
    setEditingDisbursement(null);
    fileUpload.resetFiles();
    
    // Pre-fill sanctioned amount from lead if available, otherwise from first disbursement
    const existingSanctionedAmount = lead.sanctionedAmount 
      || lead.disbursements?.[0]?.sanctionedAmount 
      || 0;
    
    setFormData({
      sanctionedAmount: existingSanctionedAmount.toString(),
      disbursedAmount: type === 'full' ? remainingAmount.toString() : "",
      disbursementDate: "",
      referenceNumber: "",
      remarks: "",
      salesConsiderationDate: "",
      vendorPayout: "",
      isFinal: type === 'full',
      bankName: "",
      bankerId: "",
      sourcingType: "self",
      vendorId: "",
    });
    setViewMode('form');
  };

  const handleBack = () => {
    setViewMode('list');
    setDisbursementType(null);
    setEditingDisbursement(null);
    fileUpload.resetFiles();
  };

  const isFormValid = () => {
    return formData.sanctionedAmount && formData.disbursedAmount && formData.disbursementDate
      && formData.bankName && formData.bankerId && formData.referenceNumber
      && (formData.sourcingType === 'self' || formData.vendorId);
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast.error("Please fill all required fields (Sanctioned Amount, Disbursed Amount, Date, Bank Name, Banker Name, Reference Number, and Vendor if vendor sourced)");
      return;
    }

    const newSanctionedAmount = Number(formData.sanctionedAmount);
    const disbursedAmount = Number(formData.disbursedAmount);
    
    // Validate amounts
    if (newSanctionedAmount <= 0) {
      toast.error("Sanctioned amount must be greater than 0");
      return;
    }
    
    if (disbursedAmount <= 0) {
      toast.error("Disbursed amount must be greater than 0");
      return;
    }

    // Calculate remaining with the new sanctioned amount
    const currentRemaining = newSanctionedAmount - totalDisbursed;
    
    // For new disbursements, check if it exceeds remaining
    if (!editingDisbursement && disbursedAmount > currentRemaining) {
      toast.error(`Amount exceeds remaining balance of ${formatCurrency(currentRemaining)}`);
      return;
    }

    // For edits, check if it exceeds remaining + current disbursement
    if (editingDisbursement) {
      const maxAllowed = currentRemaining + editingDisbursement.disbursedAmount;
      if (disbursedAmount > maxAllowed) {
        toast.error(`Amount exceeds available balance of ${formatCurrency(maxAllowed)}`);
        return;
      }
    }

    // Validate vendor payout
    if (formData.vendorPayout) {
      const payout = Number(formData.vendorPayout);
      if (payout < 0 || payout > 1) {
        toast.error("Vendor Payout must be between 0 and 1");
        return;
      }
    }

    // Resolve banker and vendor names for denormalization
    const selectedBanker = formData.bankerId ? bankers.find(b => b.id === formData.bankerId) : null;
    const selectedVendor = formData.vendorId ? channelPartners.find(p => p.id === formData.vendorId) : null;

    // Upload any selected files to Firebase Storage
    let uploadedDocuments: DocumentAttachment[] = editingDisbursement?.documents ? [...editingDisbursement.documents] : [];
    if (fileUpload.files.length > 0) {
      setIsUploadingFiles(true);
      try {
        const userId = user?.userId || user?.id || 'unknown';
        const newDocs = await Promise.all(
          fileUpload.files.map(file =>
            uploadFileToStorage(file, `leads/${userId}/${lead.id}/disb_${Date.now()}_${file.name}`)
          )
        );
        uploadedDocuments = [...uploadedDocuments, ...newDocs];
      } catch (err) {
        console.error('File upload error:', err);
        toast.error('Failed to upload one or more files. Please try again.');
        setIsUploadingFiles(false);
        return;
      } finally {
        setIsUploadingFiles(false);
      }
    }

    // Create or update disbursement record
    const disbursementRecord: DisbursementRecord = {
      id: editingDisbursement?.id || `disb_${Date.now()}`,
      type: disbursementType as 'full' | 'partial',
      isFinal: disbursementType === 'full' || formData.isFinal,
      disbursedAmount: disbursedAmount,
      sanctionedAmount: newSanctionedAmount,
      disbursementDate: new Date(formData.disbursementDate),
      salesConsiderationDate: formData.salesConsiderationDate 
        ? new Date(formData.salesConsiderationDate) 
        : undefined,
      referenceNumber: formData.referenceNumber || undefined,
      bankName: formData.bankName || undefined,
      bankerId: formData.bankerId || undefined,
      bankerName: selectedBanker ? selectedBanker.name : undefined,
      sourcingType: formData.sourcingType,
      vendorId: formData.sourcingType === 'vendor' ? formData.vendorId : undefined,
      vendorName: formData.sourcingType === 'vendor' && selectedVendor ? selectedVendor.name : undefined,
      vendorPayout: formData.vendorPayout ? Number(formData.vendorPayout) : undefined,
      remarks: formData.remarks || undefined,
      createdAt: editingDisbursement?.createdAt || new Date(),
      createdBy: editingDisbursement?.createdBy || user?.name || 'Unknown',
      updatedAt: editingDisbursement ? new Date() : undefined,
      updatedBy: editingDisbursement ? user?.name : undefined,
      documents: uploadedDocuments.length > 0 ? uploadedDocuments : undefined,
    };

    // Update disbursements array
    let updatedDisbursements: DisbursementRecord[];
    if (editingDisbursement) {
      // Update existing
      updatedDisbursements = (lead.disbursements || []).map(d => 
        d.id === editingDisbursement.id ? disbursementRecord : d
      );
    } else {
      // Add new
      updatedDisbursements = [...(lead.disbursements || []), disbursementRecord];
    }

    // Calculate totals
    const newTotalDisbursed = updatedDisbursements.reduce(
      (sum, d) => sum + d.disbursedAmount, 
      0
    );
    const newRemainingAmount = newSanctionedAmount - newTotalDisbursed;
    const newDisbursementProgress = newSanctionedAmount 
      ? ((newTotalDisbursed / newSanctionedAmount) * 100)
      : 0;

    // Determine new status based on total disbursed vs sanction (not manual isFinal flag)
    let newStatus = lead.status;
    const isTotalComplete = newSanctionedAmount > 0 && newTotalDisbursed >= newSanctionedAmount;

    if (isTotalComplete) {
      newStatus = 'disbursed';
      // Auto-mark isFinal only on the latest disbursement (by disbursementDate), others false
      const latestId = [...updatedDisbursements].sort(
        (a, b) => new Date(b.disbursementDate).getTime() - new Date(a.disbursementDate).getTime()
      )[0]?.id;
      updatedDisbursements = updatedDisbursements.map(d => ({ ...d, isFinal: d.id === latestId }));
    } else if (updatedDisbursements.length > 0) {
      newStatus = 'partial_disbursed';
      // Remove isFinal tick from all records if total < sanction
      updatedDisbursements = updatedDisbursements.map(d => ({ ...d, isFinal: false }));
    }

    // Create timeline entry
    const timelineEntry: TimelineItem = {
      id: `timeline_${Date.now()}`,
      type: 'note',
      content: editingDisbursement
        ? `Updated ${disbursementType} disbursement of ${formatCurrency(disbursedAmount)}`
        : `${disbursementType === 'full' ? 'Full' : 'Partial'} disbursement of ${formatCurrency(disbursedAmount)} completed${isTotalComplete ? ' (Final)' : ''}`,
      createdAt: new Date(),
      createdBy: user?.name || 'Unknown',
    };

    // Update lead
    const updatedLead: Partial<Lead> = {
      sanctionedAmount: newSanctionedAmount, // Always update sanctioned amount
      disbursements: updatedDisbursements,
      totalDisbursed: newTotalDisbursed,
      remainingAmount: newRemainingAmount,
      disbursementProgress: newDisbursementProgress,
      status: newStatus,
      timeline: [timelineEntry, ...(lead.timeline || [])],
    };

    // Auto-update lead loanAmount if sanction amount differs
    if (newSanctionedAmount && newSanctionedAmount !== lead.loanAmount) {
      updatedLead.loanAmount = newSanctionedAmount;
    }

    // Override lead banker / vendor / lender fields from this disbursement
    if (formData.bankerId) {
      updatedLead.smContactId = formData.bankerId;
    }
    if (formData.bankName) {
      updatedLead.lender = formData.bankName;
      updatedLead.assignedBank = formData.bankName;
    }
    if (formData.sourcingType === 'vendor' && formData.vendorId) {
      updatedLead.connectorId = formData.vendorId;
    }

    // Track stage change if status changed
    if (newStatus !== lead.status) {
      addLeadStageChange({
        leadId: lead.id,
        fromStatus: lead.status,
        toStatus: newStatus,
        changedBy: user?.userId || user?.id || 'Unknown',
        changedAt: new Date(),
        officeId: user?.officeId || '',
      });
    }

    onSave(updatedLead);
    
    toast.success(
      editingDisbursement 
        ? "Disbursement updated successfully" 
        : "Disbursement added successfully",
      {
        description: newStatus !== lead.status 
          ? `Status changed to ${newStatus === 'disbursed' ? 'Disbursed' : 'Partial Disbursed'}`
          : undefined
      }
    );
    
    // Show share popup after new disbursement (not for edits)
    // Queue the popup and collapse to list first — popup opens via useEffect
    if (!editingDisbursement) {
      const resolvedBanker = formData.bankerId ? bankers.find(b => b.id === formData.bankerId) : null;
      const resolvedVendor = formData.vendorId ? channelPartners.find(p => p.id === formData.vendorId) : null;
      const productLabel = lead.loanType && LOAN_TYPE_LABELS[lead.loanType] ? LOAN_TYPE_LABELS[lead.loanType] : lead.loanType || '-';
      const newPartialIndex = disbursementType === 'partial'
        ? updatedDisbursements.filter(d => d.type === 'partial').length
        : undefined;
      const sd = {
        type: disbursementType as 'partial' | 'full',
        partialIndex: newPartialIndex,
        customerName: getLegacyCustomerName(lead),
        loanAmount: formatCurrency(disbursedAmount),
        product: productLabel,
        applicationNo: formData.referenceNumber || '-',
        bankName: formData.bankName || '-',
        bankerName: resolvedBanker ? resolvedBanker.name : '-',
        sourcing: formData.sourcingType === 'self' ? 'Self' : 'Vendor',
        vendorName: formData.sourcingType === 'vendor' && resolvedVendor ? resolvedVendor.name : '',
      };
      setPendingShareData(sd);
      setViewMode('list');
      setEditingDisbursement(null);
    } else {
      // After edit, go back to list management view
      setViewMode('list');
      setEditingDisbursement(null);
    }
  };

  // Camera capture handler
  const handleCameraCapture = async () => {
    await fileUpload.handleCameraCapture();
  };

  // File picker handler
  const handleFilePickerClick = () => {
    fileUpload.handleFileUpload();
  };

  // Remove file from selection
  const handleRemoveFile = (index: number) => {
    fileUpload.handleRemoveFile(index);
  };

  const handleImageClick = (index: number) => {
    fileUpload.openImageViewer(index);
  };

  const buildShareMessage = (data: {
    type: 'partial' | 'full';
    partialIndex?: number;
    customerName: string;
    loanAmount: string;
    product: string;
    applicationNo: string;
    bankName: string;
    bankerName: string;
    sourcing: string;
    vendorName: string;
  }) => {
    const userLabel = (user?.primaryRole === 'sales_agent' && user?.name) ? user.name : 'Direct';
    const typeLabel = data.type === 'partial'
      ? `Partial #${data.partialIndex ?? ''} Disbursement`
      : 'Full Disbursement';
    let msg = `${userLabel}: ${typeLabel} Done\nCustomer Name: ${data.customerName}\nLoan Amount: ${data.loanAmount}\nProduct: ${data.product}\nApplication No: ${data.applicationNo}\nBank Name: ${data.bankName}\nBanker Name: ${data.bankerName}\nSourcing: ${data.sourcing}`;
    if (data.sourcing === 'Vendor' && data.vendorName) {
      msg += `\nVendor Name: ${data.vendorName}`;
    }
    return msg;
  };

  const handleWhatsAppShare = (message: string) => {
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  // Open share popup for an existing disbursement card
  const openShareForDisbursement = (disbursement: DisbursementRecord) => {
    const productLabel = lead.loanType && LOAN_TYPE_LABELS[lead.loanType] ? LOAN_TYPE_LABELS[lead.loanType] : lead.loanType || '-';
    // Compute 1-based position among partial disbursements for this record
    const partialDisbursements = (lead.disbursements || []).filter(d => d.type === 'partial');
    const partialIndex = disbursement.type === 'partial'
      ? partialDisbursements.findIndex(d => d.id === disbursement.id) + 1
      : undefined;
    const sd = {
      type: disbursement.type,
      partialIndex,
      customerName: `${lead.firstName} ${lead.lastName}`.trim(),
      loanAmount: formatCurrency(disbursement.disbursedAmount),
      product: productLabel,
      applicationNo: disbursement.referenceNumber || '-',
      bankName: disbursement.bankName || '-',
      bankerName: disbursement.bankerName || '-',
      sourcing: disbursement.sourcingType === 'vendor' ? 'Vendor' : 'Self',
      vendorName: disbursement.vendorName || '',
    };
    setShareData(sd);
    setShareMessage(buildShareMessage(sd));
    setSharePopupOpen(true);
  };

  // Share popup dialog (rendered in any view mode)
  const renderSharePopup = () => {
    if (!shareData) return null;
    return (
      <Dialog open={sharePopupOpen} onOpenChange={(open) => {
        setSharePopupOpen(open);
        if (!open) {
          setShareData(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DlgTitle className="text-lg font-bold">Disbursement Summary</DlgTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={shareMessage}
              onChange={(e) => setShareMessage(e.target.value)}
              rows={10}
              className="text-sm font-mono leading-relaxed resize-none"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setSharePopupOpen(false);
                  setShareData(null);
                  const last = lead.disbursements?.[lead.disbursements.length - 1];
                  if (last) handleEditDisbursement(last);
                }}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => handleWhatsAppShare(shareMessage)}
              >
                <Share2 className="h-4 w-4 mr-2" />
                WhatsApp Share
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // List View (Disbursement Management Screen)
  if (viewMode === 'list') {
    return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl overflow-y-auto">
          <SheetHeader className="pb-4">
            <SheetTitle className="text-xl font-bold">Disbursement Management</SheetTitle>
            <p className="text-sm text-muted-foreground">
              Track and manage all disbursements for this loan
            </p>
          </SheetHeader>

          <div className="space-y-5 pb-8">
            {/* Summary Card - only show if sanctionedAmount exists */}
            {sanctionedAmount > 0 && (
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Disbursement Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Sanctioned Amount:</span>
                      <span className="font-bold">{formatCurrency(sanctionedAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Total Disbursed:</span>
                      <span className="font-bold text-success">
                        {formatCurrency(totalDisbursed)}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-sm">
                      <span className="font-semibold">Remaining Amount:</span>
                      <span className="font-bold text-warning">
                        {formatCurrency(remainingAmount)}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Progress value={disbursementProgress} className="h-2" />
                    <p className="text-xs text-muted-foreground text-center">
                      {disbursementProgress.toFixed(1)}% disbursed
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Disbursement History */}
            {lead.disbursements && lead.disbursements.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Disbursement History
                </h3>
                {lead.disbursements.map((disbursement, index) => (
                  <Card key={disbursement.id} className="border-l-4 border-l-green-500">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant={disbursement.type === 'full' ? 'default' : 'secondary'}>
                              {disbursement.type === 'full' ? 'Full' : 'Partial'} #{index + 1}
                            </Badge>
                            {disbursement.isFinal && (
                              <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                                Final
                              </Badge>
                            )}
                          </div>
                          <p className="font-bold text-lg text-green-700">
                            {formatCurrency(disbursement.disbursedAmount)}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {format(disbursement.disbursementDate, 'dd MMM yyyy')}
                          </p>
                          {disbursement.referenceNumber && (
                            <p className="text-xs text-muted-foreground">
                              Ref: {disbursement.referenceNumber}
                            </p>
                          )}
                          {disbursement.bankName && (
                            <p className="text-xs text-muted-foreground">
                              Bank: {disbursement.bankName}
                            </p>
                          )}
                          {disbursement.sourcingType && (
                            <p className="text-xs text-muted-foreground">
                              {disbursement.sourcingType === 'vendor' 
                                ? `Vendor: ${disbursement.vendorName || 'N/A'}` 
                                : 'Self Sourced'}
                            </p>
                          )}
                          {disbursement.remarks && (
                            <p className="text-xs text-muted-foreground mt-1 italic">
                              {disbursement.remarks}
                            </p>
                          )}
                          {disbursement.updatedAt && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Updated by {disbursement.updatedBy} on {format(disbursement.updatedAt, 'dd MMM yyyy')}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openShareForDisbursement(disbursement)}
                            className="text-green-600"
                          >
                            <Share2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditDisbursement(disbursement)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {/* Document Attachments */}
                      {disbursement.documents && disbursement.documents.length > 0 && (
                        <div className="mt-3 pt-3 border-t space-y-2">
                          <p className="text-xs font-medium text-muted-foreground">
                            Documents ({disbursement.documents.length})
                          </p>
                          {disbursement.documents.map(doc => {
                            const isImage = doc.type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.name);
                            const fileLabel = isImage ? 'Image' : doc.type?.includes('pdf') ? 'PDF' : 'Document';
                            return (
                              <div key={doc.id} className="flex items-center gap-2 bg-muted/50 rounded-lg p-2">
                                <div className="h-8 w-8 rounded shrink-0 flex items-center justify-center bg-background overflow-hidden">
                                  {isImage ? (
                                    <img src={doc.url} alt={doc.name} className="h-8 w-8 object-cover" />
                                  ) : (
                                    <FileText className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium truncate">{doc.name}</p>
                                  <p className="text-xs text-muted-foreground">{fileLabel}</p>
                                </div>
                                <div className="flex items-center gap-0.5 shrink-0">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    title="View"
                                    onClick={() => window.open(doc.url, '_blank')}
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                  </Button>
                                  <a
                                    href={doc.url}
                                    download={doc.name}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-accent/10 transition-colors"
                                    title="Download"
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                  </a>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Add New Disbursement Section - Always show */}
            {(!sanctionedAmount || remainingAmount > 0) && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    ₹
                  </div>
                  <h3 className="font-semibold text-sm">
                    {!sanctionedAmount ? 'Add First Disbursement' : 'Add New Disbursement'}
                  </h3>
                </div>
                
                <div className="space-y-3">
                  {/* Show Full Disbursement option only if no partial disbursements exist */}
                  {!hasPartialDisbursements && (
                    <Card 
                      className="cursor-pointer hover:shadow-md transition-all border hover:border-primary/50"
                      onClick={() => handleAddNew('full')}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-100 text-green-700 shrink-0">
                            ₹
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold">Full Disbursement</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {!sanctionedAmount ? 'Set sanctioned amount and disburse' : 'Disburse complete remaining amount'}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  <Card 
                    className="cursor-pointer hover:shadow-md transition-all border hover:border-primary/50"
                    onClick={() => handleAddNew('partial')}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700 shrink-0">
                          <TrendingUp className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold">Partial Disbursement</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {!sanctionedAmount ? 'Set sanctioned amount and disburse in tranches' : 'Disburse in multiple tranches'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {sanctionedAmount > 0 && remainingAmount === 0 && lead.disbursements && lead.disbursements.length > 0 && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4 text-center">
                  <p className="text-sm font-semibold text-green-800">
                    ✓ Loan Fully Disbursed
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Total disbursed: {formatCurrency(totalDisbursed)}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </SheetContent>
      </Sheet>
      {renderSharePopup()}
    </>
    );
  }

  // Form View (Add/Edit Disbursement)
  return (
  <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl overflow-y-auto">
        <SheetHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="shrink-0 -ml-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1">
              <SheetTitle className="text-xl font-bold">
                {editingDisbursement ? 'Edit' : 'Add'} {disbursementType === 'full' ? 'Full' : 'Partial'} Disbursement
              </SheetTitle>
              <p className="text-sm text-muted-foreground">
                {editingDisbursement ? 'Update disbursement details' : 'Enter disbursement information'}
              </p>
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-5 pb-8">
          {/* Disbursement Type Badge */}
          <div className="space-y-3">
            <Badge variant={disbursementType === 'full' ? 'default' : 'secondary'} className="text-sm">
              {disbursementType === 'full' ? 'Full Disbursement' : 'Partial Disbursement'}
            </Badge>
            {disbursementType === 'partial' && (  // Removed manual isFinal checkbox — now auto-computed on save
            <div />
          )}
          </div>

          {/* Sanctioned Amount - FIRST FIELD */}
          <div className="space-y-2">
            <Label htmlFor="sanctionedAmount">
              Sanctioned Amount (₹) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="sanctionedAmount"
              type="text"
              placeholder="Ex - 50,00,000"
              value={formData.sanctionedAmount ? formatIndianNumber(formData.sanctionedAmount) : ''}
              onChange={(e) => {
                const rawValue = parseIndianNumber(e.target.value);
                setFormData({ ...formData, sanctionedAmount: rawValue });
              }}
              onWheel={(e) => e.currentTarget.blur()}
              className="h-12 placeholder:text-muted-foreground/50"
            />
            <p className="text-xs text-muted-foreground">
              The total loan amount sanctioned by the bank
            </p>
          </div>

          {/* Amount Summary Card - Only show if sanctioned amount is filled */}
          {formData.sanctionedAmount && Number(formData.sanctionedAmount) > 0 && (
            <Card className="bg-muted/30">
              <CardContent className="p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Sanctioned Amount:</span>
                  <span className="font-bold">{formatCurrency(Number(formData.sanctionedAmount))}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Already Disbursed:</span>
                  <span>{formatCurrency(totalDisbursed)}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="font-semibold">Remaining Amount:</span>
                  <span className="font-bold text-success">
                    {formatCurrency(Number(formData.sanctionedAmount) - totalDisbursed)}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Disbursement Amount */}
          <div className="space-y-2">
            <Label htmlFor="disbursedAmount">
              Disbursement Amount (₹) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="disbursedAmount"
              type="text"
              placeholder={formData.sanctionedAmount ? `Ex - ${formatIndianNumber(Number(formData.sanctionedAmount) - totalDisbursed)}` : "Ex - 50,00,000"}
              value={formData.disbursedAmount ? formatIndianNumber(formData.disbursedAmount) : ''}
              onChange={(e) => {
                const rawValue = parseIndianNumber(e.target.value);
                setFormData({ ...formData, disbursedAmount: rawValue });
              }}
              onWheel={(e) => e.currentTarget.blur()}
              className="h-12 placeholder:text-muted-foreground/50"
            />
            {disbursementType === 'full' && formData.sanctionedAmount && (
              <Button
                variant="link"
                size="sm"
                className="p-0 h-auto text-xs"
                onClick={() => setFormData({ 
                  ...formData, 
                  disbursedAmount: (Number(formData.sanctionedAmount) - totalDisbursed).toString()
                })}
              >
                Use full remaining amount ({formatCurrency(Number(formData.sanctionedAmount) - totalDisbursed)})
              </Button>
            )}
          </div>

          {/* Disbursement Date */}
          <div className="space-y-2">
            <Label htmlFor="disbursementDate">Disbursement Date <span className="text-red-500">*</span></Label>
            <Input
              id="disbursementDate"
              type="date"
              value={formData.disbursementDate}
              onChange={(e) => setFormData({ ...formData, disbursementDate: e.target.value })}
              className="h-12"
            />
          </div>

          {/* Bank Name - NEW COMPULSORY FIELD */}
          <div className="space-y-2">
            <Label>Bank Name <span className="text-red-500">*</span></Label>
            <SearchableSelect
              options={config.banks.map(bank => ({ value: bank, label: bank }))}
              value={formData.bankName}
              onValueChange={(v) => setFormData({ ...formData, bankName: v })}
              placeholder="Select bank name"
              searchPlaceholder="Search banks..."
              emptyText="No bank found."
            />
          </div>

          {/* Banker Name - COMPULSORY */}
          <div className="space-y-2">
            <Label>Banker Name <span className="text-red-500">*</span></Label>
            <SearchableSelect
              options={[
                ...[...bankers]
                  .sort((a, b) => {
                    const aFav = isBankerFavorite(a.id);
                    const bFav = isBankerFavorite(b.id);
                    if (aFav && !bFav) return -1;
                    if (!aFav && bFav) return 1;
                    return a.name.localeCompare(b.name);
                  })
                  .map(banker => ({
                    value: banker.id,
                    label: `${isBankerFavorite(banker.id) ? '★ ' : ''}${banker.name} - ${banker.bank}`,
                    displayLabel: banker.name
                  }))
              ]}
              value={formData.bankerId || ""}
              onValueChange={(v) => setFormData({ ...formData, bankerId: v })}
              placeholder="Select banker name"
              searchPlaceholder="Search bankers..."
              emptyText="No banker found."
            />
          </div>

          {/* Bank Reference/UTR Number - NOW COMPULSORY */}
          <div className="space-y-2">
            <Label htmlFor="referenceNumber">Bank Reference/UTR Number <span className="text-red-500">*</span></Label>
            <Input
              id="referenceNumber"
              placeholder="Ex - HDFC2024012612345"
              value={formData.referenceNumber}
              onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
              className="h-12 placeholder:text-muted-foreground/50"
            />
          </div>

          {/* Self Sourced / Vendor Sourced - NEW FIELD */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Sourcing Type <span className="text-red-500">*</span></Label>
            <div className="flex gap-3">
              <div
                className={`flex-1 p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${
                  formData.sourcingType === 'self'
                    ? 'border-primary bg-primary/5 font-semibold'
                    : 'border-border hover:border-primary/30'
                }`}
                onClick={() => setFormData({ ...formData, sourcingType: 'self', vendorId: '' })}
              >
                <span className="text-sm">Self Sourced</span>
              </div>
              <div
                className={`flex-1 p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${
                  formData.sourcingType === 'vendor'
                    ? 'border-primary bg-primary/5 font-semibold'
                    : 'border-border hover:border-primary/30'
                }`}
                onClick={() => setFormData({ ...formData, sourcingType: 'vendor' })}
              >
                <span className="text-sm">Vendor Sourced</span>
              </div>
            </div>
          </div>

          {/* Vendor Dropdown - only if vendor sourced */}
          {formData.sourcingType === 'vendor' && (
            <div className="space-y-2">
              <Label>Vendor (Channel Partner) <span className="text-red-500">*</span></Label>
              <SearchableSelect
                options={[
                  ...[...channelPartners]
                    .sort((a, b) => {
                      if (a.isFavorite && !b.isFavorite) return -1;
                      if (!a.isFavorite && b.isFavorite) return 1;
                      return a.name.localeCompare(b.name);
                    })
                    .map(partner => ({
                      value: partner.id,
                      label: `${partner.isFavorite ? '★ ' : ''}${partner.name} (${CONNECTOR_TYPE_LABELS[partner.type]}) - ${partner.city}`
                    }))
                ]}
                value={formData.vendorId || ""}
                onValueChange={(v) => setFormData({ ...formData, vendorId: v })}
                placeholder="Select vendor"
                searchPlaceholder="Search vendors..."
                emptyText="No vendor found."
              />
            </div>
          )}

          {/* Sales Consideration Date */}
          <div className="space-y-2">
            <Label htmlFor="salesConsiderationDate">Sales Consideration Date</Label>
            <Input
              id="salesConsiderationDate"
              type="date"
              value={formData.salesConsiderationDate}
              onChange={(e) => setFormData({ ...formData, salesConsiderationDate: e.target.value })}
              className="h-12"
            />
          </div>

          {/* Vendor Payout */}
          <div className="space-y-2">
            <Label htmlFor="vendorPayout">Vendor Payout (0 to 1)</Label>
            <Input
              id="vendorPayout"
              type="number"
              step="0.01"
              min="0"
              max="1"
              placeholder="Ex - 0.5"
              value={formData.vendorPayout}
              onChange={(e) => setFormData({ ...formData, vendorPayout: e.target.value })}
              onWheel={(e) => e.currentTarget.blur()}
              className="h-12 placeholder:text-muted-foreground/50"
            />
            {formData.vendorPayout && formData.disbursedAmount && (
              <p className="text-xs text-muted-foreground">
                Payout Amount: {formatCurrency(
                  Number(formData.disbursedAmount) * Number(formData.vendorPayout)
                )}
              </p>
            )}
          </div>

          {/* Remarks */}
          <div className="space-y-2">
            <Label htmlFor="remarks">Remarks</Label>
            <Textarea
              id="remarks"
              placeholder="Ex - First tranche for construction phase"
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              rows={3}
              className="rounded-xl placeholder:text-muted-foreground/50"
            />
          </div>

          {/* Document Upload */}
          <div className="space-y-2">
            <FileUploadSection
              files={fileUpload.files}
              onCameraCapture={handleCameraCapture}
              onFileUpload={handleFilePickerClick}
              onRemoveFile={handleRemoveFile}
              onImageClick={handleImageClick}
              imageViewerState={{
                isOpen: fileUpload.imageViewerState.isOpen,
                images: fileUpload.imageViewerState.images,
                currentIndex: fileUpload.imageViewerState.currentIndex,
                setCurrentIndex: fileUpload.imageViewerState.setCurrentIndex,
                onClose: fileUpload.closeImageViewer,
              }}
              label="Attach Documents / Photos"
              helpText="Photos, sanction letters, UTR screenshots, etc."
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-4 sticky bottom-0 bg-background pb-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleBack}
          >
            Cancel
          </Button>
          <Button
            className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600"
            onClick={handleSubmit}
            disabled={!isFormValid() || isUploadingFiles}
          >
            {isUploadingFiles ? 'Uploading...' : editingDisbursement ? 'Update' : 'Save'} Disbursement
          </Button>
        </div>
      </SheetContent>
    </Sheet>
    {renderSharePopup()}
  </>
  );
}
